import { useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';

export interface ProductCategory {
  categoryId: string;
  internalName: string;
  name: string;
  description?: string; // Make description optional
}

// Types for the variants API response
export interface ProductReference {
  internalName: string;
  productCode: string;
  categories?: ProductCategory[]; // Add the categories property to the product reference
}

export interface Price {
  amount: number;
  currencyCode: string;
}

export interface NamedPrice {
  name: string;
  amount: number;
}

export interface AuthoringPrice {
  amount: number;
  namedPrice: NamedPrice;
}

export interface OptionType {
  optionTypeId: string;
  optionTypeCode: string;
  internalName: string;
}

export interface OptionValue {
  optionValueId: string;
  optionValueCode: string;
  internalName: string;
}

export interface SelectedOption {
  optionType: OptionType;
  optionValue: OptionValue;
}

export interface Variant {
  variantId: string;
  product: ProductReference;
  productInternalName: string;
  modifiable: boolean;
  maxAllowedModifierWeight: number;
  brandTags: string[];
  promoTags: string[];
  name: string;
  variantCode: string;
  price: Price;
  authoringPrice: AuthoringPrice;
  selectedOptions: SelectedOption[];
}

export interface VariantResponse {
  variant: Variant;
}

export interface VariantsResponse {
  count: number;
  page: number;
  pages: number;
  variants: Variant[];
}

// Fetch a specific variant by ID
export const useVariant = (variantId: string) => {
  return useQuery<VariantResponse>(
    ['variant', variantId],
    async () => {
      const response = await apiClient.get<VariantResponse>(`/api/variants/${variantId}`);
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      enabled: !!variantId, // Only run if variantId is provided
    }
  );
};

// Fetch variants (with pagination)
export const useVariants = (page: number = 0, pageSize: number = 100) => {
  return useQuery<VariantsResponse>(
    ['variants', page, pageSize],
    async () => {
      const response = await apiClient.get<VariantsResponse>('/api/variants', {
        params: { page, size: pageSize }
      });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

// Format variant display data
// export const formatVariantDisplay = (variant: Variant): {
//   displayName: string;
//   formattedPrice: string;
//   optionsDisplay: string;
// } => {
//   // Extract selected options for display
//   const optionsDisplay = variant.selectedOptions
//     .filter(option => option.optionType.internalName !== 'No Option')
//     .map(option => option.optionValue.internalName)
//     .join(', ');
  
//   // Create display name
//   const displayName = variant.name || variant.productInternalName;
  
//   // Format price
//   const formattedPrice = new Intl.NumberFormat('en-US', {
//     style: 'currency',
//     currency: variant.price.currencyCode,
//     minimumFractionDigits: 2
//   }).format(variant.price.amount / 100); // Assuming price is in cents
  
//   return {
//     displayName,
//     formattedPrice,
//     optionsDisplay
//   };
// };

// Format variant data for display
export const formatVariantInfo = (variant: Variant): {
  variantId: string;
  productName: string;
  productInternalName: string;
  variantDescription: string;
  fullDescription: string;
  price: number;
  categories: ProductCategory[];
  options: Array<{
    optionType: string;
    optionValue: string;
  }>;
} => {
  // Build human-readable variant description
  const options = (variant.selectedOptions ?? [])
    .filter(option => option.optionType.internalName !== 'No Option')
    .map(option => ({
      optionType: option.optionType.internalName,
      optionValue: option.optionValue.internalName
    }));
  
  // Create a descriptive string from options
  const optionDescriptions = options.map(opt => opt.optionValue).filter(val => val);
  const variantDescription = optionDescriptions.length > 0 
    ? optionDescriptions.join(', ') 
    : 'Standard';
  
  // Full description combines product name and variant details
  const fullDescription = optionDescriptions.length > 0
    ? `${variant.product.internalName} (${variantDescription})`
    : variant.product.internalName;
  
  return {
    variantId: variant.variantId,
    productName: variant.product.internalName,
    productInternalName: variant.productInternalName,
    variantDescription,
    fullDescription,
    price: variant.price.amount,
    categories: variant.product.categories || [], // Use categories from product
    options
  };
};

// Hook for prioritized variant loading - loads specified variants first
export const usePrioritizedVariantInfo = (neededVariantIds: string[] = []) => {
  // First, try to get just the variants we need immediately
  const { data: priorityVariants, isLoading: isPriorityLoading } = useQuery<Variant[]>(
    ['priority-variants', neededVariantIds.slice(0, 20).sort()], // Sort for consistent caching
    async () => {
      if (neededVariantIds.length === 0) return [];
      
      // We now have a direct endpoint that takes variant IDs
      const priorityVariants: Variant[] = [];
      
      // Fetch variants in batches of 10
      const batches = [];
      for (let i = 0; i < neededVariantIds.length; i += 10) {
        batches.push(neededVariantIds.slice(i, i + 10));
      }
      
      for (const batch of batches) {
        const results = await Promise.all(
          batch.map(id => 
            apiClient.get<VariantResponse>(`/api/variants/${id}`)
              .then(response => response.data.variant)
              .catch(error => {
                console.error(`Error fetching variant ${id}:`, error);
                return null;
              })
          )
        );
        
        // Filter out null results and cast to Variant[]
        priorityVariants.push(...results.filter((variant): variant is Variant => variant !== null));
      }
      
      return priorityVariants;
    },
    {
      enabled: neededVariantIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes - longer cache time
      refetchOnWindowFocus: false, // Avoid unnecessary refetches
    }
  );
  
  // Build a map for quick lookup and transform to VariantInfo format
  const variantMap = new Map<string, ReturnType<typeof formatVariantInfo>>();
  
  if (priorityVariants) {
    priorityVariants.forEach(variant => {
      variantMap.set(variant.variantId, formatVariantInfo(variant));
    });
  }
  
  return {
    variantMap,
    isLoading: isPriorityLoading && !priorityVariants,
    hasPriorityData: !!priorityVariants,
    priorityLoadProgress: priorityVariants ? 
      neededVariantIds.filter((id: string) => variantMap.has(id)).length / Math.max(neededVariantIds.length, 1) : 0,
    totalVariantsLoaded: variantMap.size,
    neededVariantsFound: neededVariantIds.filter(id => variantMap.has(id)).length
  };
};
